<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref, reactive, computed, onMounted } from 'vue';
import nexceoSettingsAPI from 'dashboard/api/nexceo/settings';

const isLoading = ref(false);
const isSaving = ref(false);
const saveSuccess = ref(false);
const saveError = ref(null);
const loadError = ref(null);
const advancedOpen = ref(false);
let successTimer = null;

const form = reactive({
  company_ai_enabled: true,
  company_ai_display_name: 'ZOYA',
  company_ai_role_title: 'AI Sales Coordinator',
  company_ai_tone: 'professional',
  company_ai_intro_template: '',
  company_ai_auto_reply_enabled: true,
  company_ai_handoff_enabled: true,
  company_ai_max_replies: 10,
  company_ai_languages: ['en', 'hi'],
  company_ai_system_instructions: '',
  company_ai_response_style: '',
  company_ai_out_of_scope_reply: '',
  company_ai_handoff_instructions: '',
  company_name: '',
});

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'formal', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'or', label: 'Odia' },
  { value: 'hinglish', label: 'Hinglish' },
  { value: 'mr', label: 'Marathi' },
  { value: 'bn', label: 'Bengali' },
  { value: 'te', label: 'Telugu' },
  { value: 'ta', label: 'Tamil' },
];

const CAPABILITIES = [
  { label: 'Customer greeting & introduction', ok: true },
  { label: 'Requirement collection (BHK, budget, location)', ok: true },
  { label: 'Project identification & routing', ok: true },
  { label: 'Lead creation & update', ok: true },
  { label: 'Junk / spam detection', ok: true },
  { label: 'Human handoff escalation', ok: true },
  { label: 'Brochure / document sharing', ok: false },
  { label: 'Site visit scheduling', ok: false },
  { label: 'Google Meet creation', ok: false },
  { label: 'Inventory queries', ok: false },
];

const aiName = computed(() => form.company_ai_display_name || 'ZOYA');
const aiRole = computed(
  () => form.company_ai_role_title || 'AI Sales Coordinator'
);
const aiInitial = computed(() => aiName.value.charAt(0).toUpperCase());

const advancedFields = computed(() => [
  {
    key: 'company_ai_system_instructions',
    label: 'Admin Instructions',
    placeholder: `Additional rules injected into every ${aiName.value} system prompt. E.g. "Always greet by first name."`,
    hint: `Appended to ${aiName.value}'s system prompt on every conversation.`,
  },
  {
    key: 'company_ai_out_of_scope_reply',
    label: 'Out-of-Scope Reply',
    placeholder:
      'I can help you with project details, pricing, and visit scheduling. What are you looking for?',
    hint: 'Sent when the customer asks something outside real-estate scope.',
  },
  {
    key: 'company_ai_response_style',
    label: 'Response Style',
    placeholder:
      'Keep replies under 3 sentences. Use friendly, conversational Hinglish.',
    hint: 'Override the default response style. Leave blank for the standard format.',
  },
  {
    key: 'company_ai_handoff_instructions',
    label: 'Handoff Instructions',
    placeholder: 'When handing off, say: "Our team will contact you shortly."',
    hint: `How ${aiName.value} should behave when escalating to a human agent.`,
  },
]);

const previewMessage = computed(() => {
  const company = form.company_name || 'your company';
  if (form.company_ai_intro_template) {
    return form.company_ai_intro_template
      .replace(/\{\{ai_name\}\}/g, aiName.value)
      .replace(/\{\{role_title\}\}/g, aiRole.value)
      .replace(/\{\{company_name\}\}/g, company);
  }
  return `Hi, I'm ${aiName.value}, ${aiRole.value} at ${company}. I can help you find the right property based on your location, budget, and requirement. What are you looking for?`;
});

function toggleLanguage(val) {
  const idx = form.company_ai_languages.indexOf(val);
  if (idx === -1) form.company_ai_languages.push(val);
  else form.company_ai_languages.splice(idx, 1);
}

async function load() {
  isLoading.value = true;
  loadError.value = null;
  try {
    const { data } = await nexceoSettingsAPI.get();
    const s = data.settings || {};
    const keys = [
      'company_ai_enabled',
      'company_ai_display_name',
      'company_ai_role_title',
      'company_ai_tone',
      'company_ai_intro_template',
      'company_ai_auto_reply_enabled',
      'company_ai_handoff_enabled',
      'company_ai_max_replies',
      'company_ai_languages',
      'company_ai_system_instructions',
      'company_ai_response_style',
      'company_ai_out_of_scope_reply',
      'company_ai_handoff_instructions',
      'company_name',
    ];
    keys.forEach(k => {
      if (s[k] !== undefined && s[k] !== null) form[k] = s[k];
    });
    if (!Array.isArray(form.company_ai_languages))
      form.company_ai_languages = ['en', 'hi'];
    if (!form.company_ai_display_name) form.company_ai_display_name = 'ZOYA';
    if (!form.company_ai_role_title)
      form.company_ai_role_title = 'AI Sales Coordinator';
    if (!form.company_ai_tone) form.company_ai_tone = 'professional';
    if (!form.company_ai_max_replies) form.company_ai_max_replies = 10;
  } catch {
    loadError.value = 'Failed to load Company AI settings.';
  } finally {
    isLoading.value = false;
  }
}

async function save() {
  isSaving.value = true;
  saveError.value = null;
  saveSuccess.value = false;
  try {
    await nexceoSettingsAPI.update({
      company_ai_enabled: form.company_ai_enabled,
      company_ai_display_name: form.company_ai_display_name || 'ZOYA',
      company_ai_role_title:
        form.company_ai_role_title || 'AI Sales Coordinator',
      company_ai_tone: form.company_ai_tone,
      company_ai_intro_template: form.company_ai_intro_template || null,
      company_ai_auto_reply_enabled: form.company_ai_auto_reply_enabled,
      company_ai_handoff_enabled: form.company_ai_handoff_enabled,
      company_ai_max_replies: Number(form.company_ai_max_replies) || 10,
      company_ai_languages: form.company_ai_languages,
      company_ai_system_instructions:
        form.company_ai_system_instructions || null,
      company_ai_response_style: form.company_ai_response_style || null,
      company_ai_out_of_scope_reply: form.company_ai_out_of_scope_reply || null,
      company_ai_handoff_instructions:
        form.company_ai_handoff_instructions || null,
    });
    saveSuccess.value = true;
    clearTimeout(successTimer);
    successTimer = setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
    await load();
  } catch (e) {
    saveError.value = e?.response?.data?.error || 'Failed to save settings.';
  } finally {
    isSaving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- ── Page header ──────────────────────────────────────────────────────── -->
    <div
      class="flex items-center gap-3 px-5 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <div
        class="flex items-center justify-center w-7 h-7 rounded-full bg-woot-100 dark:bg-woot-500/20 shrink-0"
      >
        <span class="i-lucide-bot size-3.5 text-woot-600 dark:text-woot-400" />
      </div>
      <div class="flex-1 min-w-0">
        <h1
          class="text-sm font-semibold text-n-slate-12 leading-tight truncate"
        >
          Company AI Coordinator
        </h1>
        <p class="text-xs text-n-slate-7 leading-tight hidden sm:block">
          First responder for new leads — collects requirements and routes to
          the right Project AI
        </p>
      </div>

      <!-- Status pill -->
      <span
        class="hidden sm:inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border shrink-0 transition-colors"
        :class="
          form.company_ai_enabled
            ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30'
            : 'bg-n-alpha-2 text-n-slate-7 border-n-weak'
        "
      >
        <span
          class="size-1.5 rounded-full"
          :class="form.company_ai_enabled ? 'bg-success-500' : 'bg-n-slate-4'"
        />
        {{ form.company_ai_enabled ? 'Active' : 'Disabled' }}
      </span>

      <button
        :disabled="isSaving || isLoading"
        class="flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors shrink-0"
        @click="save"
      >
        <span
          class="size-3.5"
          :class="isSaving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-save'"
        />
        Save
      </button>
    </div>

    <!-- ── Toast banners ────────────────────────────────────────────────────── -->
    <transition name="toast">
      <div
        v-if="saveSuccess"
        class="flex items-center gap-2 px-5 py-2.5 bg-success-50 dark:bg-success-800/20 border-b border-success-200 dark:border-success-500/30 text-sm text-success-800 dark:text-success-400 shrink-0"
      >
        <span class="i-lucide-check-circle size-4 shrink-0" />
        Settings saved successfully.
        <button
          class="ml-auto i-lucide-x size-4 opacity-50 hover:opacity-100"
          @click="saveSuccess = false"
        />
      </div>
    </transition>
    <div
      v-if="saveError"
      class="flex items-center gap-2 px-5 py-2.5 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 dark:border-ruby-500/30 text-sm text-ruby-700 dark:text-ruby-400 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ saveError }}
      <button
        class="ml-auto i-lucide-x size-4 opacity-50 hover:opacity-100"
        @click="saveError = null"
      />
    </div>

    <!-- ── Loading ───────────────────────────────────────────────────────────── -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500" />
        <span class="text-sm">Loading…</span>
      </div>
    </div>

    <!-- ── Load error ────────────────────────────────────────────────────────── -->
    <div
      v-else-if="loadError"
      class="flex-1 flex items-center justify-center p-6"
    >
      <div class="text-center">
        <span
          class="i-lucide-alert-circle size-8 text-ruby-500 mx-auto block mb-2"
        />
        <p class="text-sm text-n-slate-11 mb-3">{{ loadError }}</p>
        <button class="text-sm text-woot-500 hover:underline" @click="load">
          Try again
        </button>
      </div>
    </div>

    <!-- ── Main content ──────────────────────────────────────────────────────── -->
    <div v-else class="flex-1 overflow-y-auto">
      <div class="px-5 py-5 max-w-6xl mx-auto">
        <div class="grid grid-cols-1 xl:grid-cols-12 gap-5">
          <!-- ══════════════════════════════════
               LEFT COLUMN  xl:col-span-7
               ══════════════════════════════════ -->
          <div class="xl:col-span-7 space-y-4">
            <!-- ── Enable / Disable ─────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl"
            >
              <div class="px-5 py-4 flex items-center gap-4">
                <div
                  class="flex items-center justify-center w-9 h-9 rounded-xl shrink-0"
                  :class="
                    form.company_ai_enabled
                      ? 'bg-woot-50 dark:bg-woot-500/10'
                      : 'bg-n-alpha-2'
                  "
                >
                  <span
                    class="size-4"
                    :class="
                      form.company_ai_enabled
                        ? 'i-lucide-bot text-woot-500'
                        : 'i-lucide-bot-off text-n-slate-5'
                    "
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-n-slate-12">
                    Enable Company AI
                  </p>
                  <p class="text-xs text-n-slate-7 mt-0.5">
                    {{ aiName }} auto-replies to leads not yet matched to a
                    project
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                  :class="
                    form.company_ai_enabled ? 'bg-woot-500' : 'bg-n-slate-4'
                  "
                  @click="form.company_ai_enabled = !form.company_ai_enabled"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                    :class="
                      form.company_ai_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </section>

            <!-- ── AI Identity ──────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-user-circle size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  AI Identity
                </h2>
              </div>
              <div class="px-5 py-5 space-y-4">
                <!-- Avatar + name row -->
                <div
                  class="flex items-center gap-3 p-3 rounded-xl bg-woot-50 dark:bg-woot-500/10 border border-woot-100 dark:border-woot-500/20"
                >
                  <div
                    class="w-10 h-10 rounded-full bg-woot-500 flex items-center justify-center text-white font-bold text-sm shrink-0"
                  >
                    {{ aiInitial }}
                  </div>
                  <div class="min-w-0">
                    <p
                      class="text-sm font-semibold text-woot-700 dark:text-woot-300 truncate"
                    >
                      {{ aiName }}
                    </p>
                    <p class="text-xs text-woot-500 truncate">{{ aiRole }}</p>
                  </div>
                  <span
                    class="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-woot-100 dark:bg-woot-500/20 text-woot-600 dark:text-woot-300 font-medium shrink-0"
                  >
                    Company AI
                  </span>
                </div>

                <!-- Fields -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1.5"
                      >Display Name</label
                    >
                    <input
                      v-model="form.company_ai_display_name"
                      type="text"
                      placeholder="ZOYA"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-6 focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                    />
                    <p class="text-xs text-n-slate-6 mt-1">
                      Shown to customers in every reply
                    </p>
                  </div>
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1.5"
                      >Role Title</label
                    >
                    <input
                      v-model="form.company_ai_role_title"
                      type="text"
                      placeholder="AI Sales Coordinator"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-6 focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                    />
                    <p class="text-xs text-n-slate-6 mt-1">
                      Shown alongside display name
                    </p>
                  </div>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1.5"
                      >Tone</label
                    >
                    <select
                      v-model="form.company_ai_tone"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                    >
                      <option
                        v-for="t in TONES"
                        :key="t.value"
                        :value="t.value"
                      >
                        {{ t.label }}
                      </option>
                    </select>
                  </div>
                  <div>
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1.5"
                      >Max Auto-Replies</label
                    >
                    <input
                      v-model.number="form.company_ai_max_replies"
                      type="number"
                      min="1"
                      max="50"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                    />
                    <p class="text-xs text-n-slate-6 mt-1">
                      AI pauses after this limit
                    </p>
                  </div>
                </div>
                <div>
                  <label
                    class="block text-xs font-medium text-n-slate-10 mb-1.5"
                  >
                    Introduction Template
                    <span class="text-n-slate-6 font-normal ml-1"
                      >(optional)</span
                    >
                  </label>
                  <textarea
                    v-model="form.company_ai_intro_template"
                    rows="2"
                    placeholder="Hi, I'm {{ai_name}}, {{role_title}} at {{company_name}}. How can I help you today?"
                    class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-6 focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors resize-none"
                  />
                  <p class="text-xs text-n-slate-6 mt-1">
                    Available variables:
                    <code
                      class="bg-n-alpha-2 px-1 py-0.5 rounded text-[10px]"
                      >{{ ai_name }}</code
                    >
                    <code
                      class="bg-n-alpha-2 px-1 py-0.5 rounded text-[10px] ml-1"
                      >{{ role_title }}</code
                    >
                    <code
                      class="bg-n-alpha-2 px-1 py-0.5 rounded text-[10px] ml-1"
                      >{{ company_name }}</code
                    >
                  </p>
                </div>
              </div>
            </section>

            <!-- ── Languages ────────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-globe size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Languages
                </h2>
                <span class="ml-auto text-xs text-n-slate-6"
                  >{{ form.company_ai_languages.length }} selected</span
                >
              </div>
              <div class="px-5 py-4">
                <p class="text-xs text-n-slate-7 mb-3">
                  {{ aiName }} auto-detects the customer's language and switches
                  naturally.
                </p>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="lang in LANGUAGE_OPTIONS"
                    :key="lang.value"
                    class="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                    :class="
                      form.company_ai_languages.includes(lang.value)
                        ? 'bg-woot-500 text-white border-woot-500 shadow-sm'
                        : 'bg-n-background text-n-slate-8 border-n-weak hover:border-woot-300 hover:text-woot-600'
                    "
                    @click="toggleLanguage(lang.value)"
                  >
                    {{ lang.label }}
                  </button>
                </div>
              </div>
            </section>

            <!-- ── Behaviour ────────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-settings-2 size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Behaviour
                </h2>
              </div>
              <div class="divide-y divide-n-weak">
                <!-- Auto-reply -->
                <div class="px-5 py-4 flex items-center gap-4">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-alpha-2 shrink-0"
                  >
                    <span
                      class="i-lucide-message-square-dot size-3.5 text-n-slate-7"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12">
                      Auto-Reply
                    </p>
                    <p class="text-xs text-n-slate-7">
                      {{ aiName }} automatically replies to incoming messages
                    </p>
                  </div>
                  <button
                    class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                    :class="
                      form.company_ai_auto_reply_enabled
                        ? 'bg-woot-500'
                        : 'bg-n-slate-4'
                    "
                    @click="
                      form.company_ai_auto_reply_enabled =
                        !form.company_ai_auto_reply_enabled
                    "
                  >
                    <span
                      class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                      :class="
                        form.company_ai_auto_reply_enabled
                          ? 'translate-x-4'
                          : 'translate-x-0.5'
                      "
                    />
                  </button>
                </div>
                <!-- Human handoff -->
                <div class="px-5 py-4 flex items-center gap-4">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-alpha-2 shrink-0"
                  >
                    <span class="i-lucide-user-check size-3.5 text-n-slate-7" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12">
                      Human Handoff
                    </p>
                    <p class="text-xs text-n-slate-7">
                      {{ aiName }} can escalate to a human agent when needed
                    </p>
                  </div>
                  <button
                    class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0"
                    :class="
                      form.company_ai_handoff_enabled
                        ? 'bg-woot-500'
                        : 'bg-n-slate-4'
                    "
                    @click="
                      form.company_ai_handoff_enabled =
                        !form.company_ai_handoff_enabled
                    "
                  >
                    <span
                      class="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm"
                      :class="
                        form.company_ai_handoff_enabled
                          ? 'translate-x-4'
                          : 'translate-x-0.5'
                      "
                    />
                  </button>
                </div>
              </div>
            </section>

            <!-- ── Advanced Instructions (collapsible) ──────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <button
                class="w-full px-5 py-3 flex items-center gap-2 hover:bg-n-alpha-1 transition-colors"
                @click="advancedOpen = !advancedOpen"
              >
                <span
                  class="i-lucide-scroll-text size-3.5 text-n-slate-7 shrink-0"
                />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider flex-1 text-left"
                >
                  Advanced Instructions
                </h2>
                <span class="text-xs text-n-slate-6 font-normal">optional</span>
                <span
                  class="size-4 text-n-slate-6 transition-transform shrink-0"
                  :class="
                    advancedOpen
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>

              <div v-if="advancedOpen" class="border-t border-n-weak">
                <div class="px-5 py-5 space-y-5">
                  <div v-for="field in advancedFields" :key="field.key">
                    <label
                      class="block text-xs font-medium text-n-slate-10 mb-1.5"
                      >{{ field.label }}</label
                    >
                    <textarea
                      v-model="form[field.key]"
                      rows="3"
                      :placeholder="field.placeholder"
                      class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-6 focus:outline-none focus:ring-1 focus:ring-woot-500 focus:border-woot-500 transition-colors resize-none"
                    />
                    <p class="text-xs text-n-slate-6 mt-1">{{ field.hint }}</p>
                  </div>
                </div>
              </div>
            </section>

            <!-- ── Save button ──────────────────────────────────────────── -->
            <div class="flex justify-end pb-1">
              <button
                :disabled="isSaving"
                class="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="save"
              >
                <span
                  class="size-4"
                  :class="
                    isSaving
                      ? 'i-lucide-loader-2 animate-spin'
                      : 'i-lucide-save'
                  "
                />
                Save Settings
              </button>
            </div>
          </div>
          <!-- /left column -->

          <!-- ══════════════════════════════════
               RIGHT COLUMN  xl:col-span-5
               ══════════════════════════════════ -->
          <div class="xl:col-span-5 space-y-4">
            <!-- ── Live Preview ─────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-eye size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Live Preview
                </h2>
                <span
                  class="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-7 font-medium"
                >
                  Updates as you type
                </span>
              </div>
              <div class="px-5 py-4">
                <!-- Mock conversation -->
                <div class="space-y-3">
                  <!-- Customer message -->
                  <div class="flex justify-end">
                    <div
                      class="bg-n-alpha-2 rounded-xl rounded-br-sm px-3.5 py-2.5 max-w-[80%]"
                    >
                      <p class="text-xs text-n-slate-9">
                        Hi, I'm looking for a 3BHK apartment
                      </p>
                    </div>
                  </div>
                  <!-- AI reply -->
                  <div class="flex items-start gap-2.5">
                    <div
                      class="w-7 h-7 rounded-full bg-woot-500 flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5"
                    >
                      {{ aiInitial }}
                    </div>
                    <div
                      class="flex-1 bg-woot-50 dark:bg-woot-500/10 border border-woot-100 dark:border-woot-500/20 rounded-xl rounded-tl-sm px-3.5 py-3"
                    >
                      <p
                        class="text-[11px] font-semibold text-woot-600 dark:text-woot-400 mb-1.5"
                      >
                        {{ aiName }}
                        <span class="font-normal text-woot-400 ml-1">{{
                          aiRole
                        }}</span>
                      </p>
                      <p class="text-xs text-n-slate-11 leading-relaxed">
                        {{ previewMessage }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Meta row -->
                <div
                  class="flex items-center gap-3 mt-4 pt-3 border-t border-n-weak"
                >
                  <div
                    class="flex items-center gap-1.5 text-[10px] text-n-slate-6"
                  >
                    <span class="i-lucide-volume-2 size-3" />
                    <span class="capitalize">{{ form.company_ai_tone }}</span>
                  </div>
                  <div
                    class="flex items-center gap-1.5 text-[10px] text-n-slate-6"
                  >
                    <span class="i-lucide-globe size-3" />
                    <span>{{ form.company_ai_languages.join(', ') }}</span>
                  </div>
                  <div
                    class="flex items-center gap-1.5 text-[10px] text-n-slate-6 ml-auto"
                  >
                    <span class="i-lucide-repeat size-3" />
                    <span>Max {{ form.company_ai_max_replies }} replies</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- ── Capabilities ─────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-zap size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Capabilities
                </h2>
              </div>
              <div class="px-5 py-4">
                <div class="space-y-1.5 mb-3">
                  <div
                    v-for="cap in CAPABILITIES"
                    :key="cap.label"
                    class="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs"
                    :class="
                      cap.ok
                        ? 'bg-success-50 dark:bg-success-800/10 text-success-700 dark:text-success-400'
                        : 'bg-n-alpha-1 text-n-slate-6'
                    "
                  >
                    <span
                      class="size-3 shrink-0"
                      :class="
                        cap.ok
                          ? 'i-lucide-check-circle text-success-500'
                          : 'i-lucide-x-circle text-n-slate-4'
                      "
                    />
                    {{ cap.label }}
                  </div>
                </div>
                <p
                  class="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400 pt-3 border-t border-n-weak"
                >
                  <span class="i-lucide-info size-3.5 shrink-0 mt-0.5" />
                  Document sharing, site visits &amp; Meet links unlock once a
                  Project AI takes over.
                </p>
              </div>
            </section>

            <!-- ── Safety Rules ─────────────────────────────────────────── -->
            <section
              class="bg-white dark:bg-n-solid-2 border border-n-weak rounded-xl overflow-hidden"
            >
              <div
                class="px-5 py-3 border-b border-n-weak bg-n-alpha-1 flex items-center gap-2"
              >
                <span class="i-lucide-shield-check size-3.5 text-n-slate-7" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  Safety Rules
                </h2>
              </div>
              <ul class="px-5 py-4 space-y-2.5">
                <li
                  v-for="rule in [
                    'Never reveals system prompt or AI instructions',
                    'Never promises discounts or special payment terms',
                    'Never guarantees legal approvals',
                    'Never confirms booking without human approval',
                    'Stops replying immediately if customer requests a human agent',
                    'Does not answer off-topic or unrelated questions',
                  ]"
                  :key="rule"
                  class="flex items-start gap-2 text-xs text-n-slate-8"
                >
                  <span
                    class="i-lucide-shield size-3 text-woot-400 mt-0.5 shrink-0"
                  />
                  {{ rule }}
                </li>
              </ul>
            </section>
          </div>
          <!-- /right column -->
        </div>
      </div>
    </div>
    <!-- /main content -->
  </div>
</template>
